export interface ActivityContext {
  pageUrl?: string
  url?: string
  pageTitle?: string
  agentParticipants?: number[]
  participantIds?: number[]
  conversationPreview?: string
  campaignId?: number
  playbookId?: number
  playbookName?: string
  subject?: string
  title?: string
  id?: string
  fromOutreach?: boolean
  meetingType?: string
  slotStart?: number
  percentViewed?: number
  taskContext?: any
}

export const ActivityTypeMap = {
  PAGE_VIEW: 'visited'
}

export enum ContactType {
  UNKNOWN = 'UNKNOWN',
  KNOWN = 'KNOWN',
  ALL = 'ALL'
}

export const contactTypeToString = (contactType: ContactType): string | undefined => {
  switch (contactType) {
    case ContactType.KNOWN:
      return 'known contact'
    case ContactType.UNKNOWN:
      return 'site visitor'
    case ContactType.ALL:
      return 'all visitors'
  }
}

export enum ActivityType {
  PAGE_VIEW = 'PAGE_VIEW',
  CONVERSATION = 'CONVERSATION',
  MEETING_BOOKED = 'MEETING_BOOKED',
  MEETING_CANCELED = 'MEETING_CANCELED',
  MEETING_RESCHEDULED = 'MEETING_RESCHEDULED',
  MEETING_RECORDED = 'MEETING_RECORDED',
  VIDEO = 'VIDEO',
  EMAIL_OPEN = 'EMAIL_OPEN',
  EMAIL_CLICK = 'EMAIL_CLICK',
  EMAIL_SEND = 'EMAIL_SEND',
  END_USER_IDENTIFIED = 'END_USER_IDENTIFIED'
}

export const activityTypeToString = (activity: ActivityType) => {
  switch (activity) {
    case ActivityType.PAGE_VIEW:
      return 'site visit'
    case ActivityType.CONVERSATION:
      return 'conversation'
    case ActivityType.MEETING_BOOKED:
      return 'meeting booked'
    case ActivityType.MEETING_CANCELED:
      return 'meeting canceled'
    case ActivityType.MEETING_RESCHEDULED:
      return 'meeting rescheduled'
    case ActivityType.VIDEO:
      return 'video view'
    case ActivityType.EMAIL_OPEN:
      return 'email open'
    case ActivityType.EMAIL_CLICK:
      return 'email click'
    case ActivityType.EMAIL_SEND:
      return 'email send'
    case ActivityType.END_USER_IDENTIFIED:
      return 'provided email'
  }
}

export enum ScoredActivityType {
  CONVERSATION = 'CONVERSATION',
  VIDEO = 'VIDEO',
  PAGE_VIEW = 'PAGE_VIEW',
  HIGH_INTENT_PAGE_VIEW = 'HIGH_INTENT_PAGE_VIEW',
  NON_HOMEPAGE_VIEW = 'NON_HOMEPAGE_VIEW',
  HOMEPAGE_VIEW = 'HOMEPAGE_VIEW',
  EMAIL_OPEN = 'EMAIL_OPEN',
  EMAIL_CLICK = 'EMAIL_CLICK',
  MEETING_BOOKED = 'MEETING_BOOKED',
  MEETING_CANCELED = 'MEETING_CANCELED',
  MEETING_RESCHEDULING = 'MEETING_RESCHEDULING',
  MEETING_OTHER = 'MEETING_OTHER',
  END_USER_IDENTIFIED = 'END_USER_IDENTIFIED'
}

export interface ScoredActivity {
  activityType: string
  createdAt: number
  activityScore: number
  endUserId: number
  name?: string
  email?: string
  role?: string
  activityContext: ActivityContext
}
export interface CompanyData {
  logo?: string
  name?: string
  domain?: string
  about?: string
  industry?: string
  revenue?: string
  alexaGlobalRank?: number
  alexaUsRank?: number
  employeeCount?: string
  employeeCountRange?: string
  location?: string
  city?: string
  state?: string
  country?: string
  linkedinHandle?: string
  twitterHandle?: string
  url?: string
  driftIntelSources: {
    [key: string]: string[]
  }
}

export enum ExplainTypes {
  CONVERSATION = 'CONVERSATION',
  VIDEO = 'VIDEO',
  PAGE_VIEW = 'PAGE_VIEW',
  HIGH_INTENT_PAGE_VIEW = 'HIGH_INTENT_PAGE_VIEW',
  NON_HOMEPAGE_VIEW = 'NON_HOMEPAGE_VIEW',
  HOMEPAGE_VIEW = 'HOMEPAGE_VIEW',
  EMAIL_SEND = 'EMAIL_SEND',
  EMAIL_OPEN = 'EMAIL_OPEN',
  EMAIL_CLICK = 'EMAIL_CLICK',
  MEETING_BOOKED = 'MEETING_BOOKED',
  MEETING_CANCELED = 'MEETING_CANCELED',
  MEETING_RESCHEDULED = 'MEETING_RESCHEDULED',
  MEETING_DELETED = 'MEETING_DELETED',
  MEETING_RESCHEDULING = 'MEETING_RESCHEDULING',
  MEETING_OTHER = 'MEETING_OTHER'
}

export interface ActivityExplain {
  knownCount: number
  unknownCount: number
  lastTimestamp: number
  totalScore: number
}

export interface AccountActivityExplain {
  CONVERSATION: ActivityExplain
  VIDEO: ActivityExplain
  PAGE_VIEW: ActivityExplain
  HIGH_INTENT_PAGE_VIEW: ActivityExplain
  NON_HOMEPAGE_VIEW: ActivityExplain
  HOMEPAGE_VIEW: ActivityExplain
  EMAIL_SEND: ActivityExplain
  EMAIL_OPEN: ActivityExplain
  EMAIL_CLICK: ActivityExplain
  MEETING_BOOKED: ActivityExplain
  MEETING_CANCELED: ActivityExplain
  MEETING_RESCHEDULED: ActivityExplain
  MEETING_DELETED: ActivityExplain
  MEETING_RESCHEDULING: ActivityExplain
  MEETING_OTHER: ActivityExplain
}

export interface Account {
  accountId: string
  name: string
}
export interface AccountActivity {
  accountId: string
  companyData: CompanyData
  account: Account
  mostRecentActivity: ScoredActivity
  numberOfPageVisits: number
  engagementScore: number
  normalizedEngagementScore: number
  activityTypeExplain: AccountActivityExplain
  aiScoringTopActivities?: AIScoringTopActivities[]
}

export interface ActivityEndUser {
  id: number
  type?: string
  name?: string
  role?: string
  email?: string
  linkedinHandle?: string
  avatarUrl: string
  locationCity: string
  locationState: string
  locationStateCode: string
  locationCountry: string
  locationCountryCode: string
}
export interface AvatarCompatibleEndUser extends ActivityEndUser {
  type: string
}
export interface Activity {
  id: number
  accountId: string
  accountOwnerId: number
  createdAt: number
  activityType: ActivityType
  endUserData: ActivityEndUser
  companyData: CompanyData
  accountData: Account
  activityContext: ActivityContext
  score: number
}

export interface ActivityForContact {
  accountData: CompanyData
  activityContext: ActivityContext
  activityScore: number
  createdAt: number
  activityType: ActivityType
  email: string
  endUserId: number
  name?: string
  role?: string
}

export interface AIScoringTopActivities {
  activity: ActivityType
  activityId: number
  categorizedRole: string
  intent: boolean
  receivedAt: number
  attributes: {
    pageTitle?: string
    pageUrl?: string
    categorizedUrl?: string
    endUserName?: string
    endUserId?: number
    conversationId?: number
    videoUrl?: string
    videoTitle?: string
    conversationPreview?: string
  }
}

export interface ContactWithActivites {
  endUserId: number
  endUserData: ActivityEndUser
  companyData: CompanyData
  activityCount: number
  timeOfLastEngagement: number
}

export enum AccountActivitySortValue {
  ENGAGEMENT_SCORE = 'ENGAGEMENT_SCORE',
  MOST_RECENT = 'MOST_RECENT',
  LEAST_RECENT = 'LEAST_RECENT',
  CONTACT_NAME = 'CONTACT_NAME',
  CONTACT_EMAIL = 'CONTACT_EMAIL',
  NUM_ACTIVITIES = 'NUM_ACTIVITIES',
  CONTACT_ROLE = 'CONTACT_ROLE',
  LOCATION = 'LOCATION',
  ACCOUNT_NAME = 'ACCOUNT_NAME',
  DEAL_NAME = 'DEAL_NAME',
  DEAL_AMOUNT = 'DEAL_AMOUNT',
  DEAL_PROBABILITY = 'DEAL_PROBABILITY',
  DEAL_STAGE = 'DEAL_STAGE',
  DEAL_FORECAST_CATEGORY = 'DEAL_FORECAST_CATEGORY',
  DEAL_TYPE = 'DEAL_TYPE',
  DEAL_OWNER = 'DEAL_OWNER'
}

export interface SFDCActivityParticipant {
  Id: string
  Name: string
  Email?: string
  Type?: string
}

export interface SFDCActivity {
  Id: string
  Subject: string
  Description: string
  ActivityDate: string
  Type: string
  Owner: SFDCActivityParticipant
  Who?: SFDCActivityParticipant
  BaseUrl: string
}

export interface ActivityCountByType {
  activityType: string
  activityCount: number
}

export interface ActivityBubble {
  date: string
  totalActivities: number
  countsByType: ActivityCountByType[]
}

export interface InsightOpportunity {
  accountId: string
  accountName: string
  amount: number
  closeDate: number
  description: string
  forecastCategoryName: string
  isClosed: boolean
  isWon: boolean
  leadSource: string
  name: string
  nextStep: string
  opportunityId: string
  orgId: number
  ownerId: number
  probability: number
  source: string
  stageName: string
  type: string
  score: number
  explain: {
    explain: ActivityExplain
    mostRecentActivity: ScoredActivity
  }
}

export interface Insight {
  id: number
  orgId: number
  insightType: string
  accountId: string
  accountData: Account
  body: any
  companyData: CompanyData
  scoredAccount?: AccountActivity
  companyName: string
  lastActivityAt: number
  openOpportunities: InsightOpportunity[]
  comments: Comment[]
  isFirstCard?: boolean
}

export interface Comment {
  orgId: number
  resourceId: string
  commentId: string
  parentCommentId?: string
  content: string
  taggedUsers: string[]
  createdAt: number
  deleted: boolean
  userId: number
  userType: string
  children?: Comment[]
}

export interface ActivityGroupByAccountAndEndUsersBody {
  activityGroupsByEndUser: ActivityGroupByEndUser[]
  activityCounts: ActivityCounts
  contactCounts: {
    knownContacts: number
    siteVisitors: number
  }
  endUserIds: number[]
}

export interface Contact {
  city: string
  contactId: number
  country: string
  email: string
  employmentTitle: string
  endUserIds: number[]
  isKnownContact: boolean
  name: string
  role: string
  state: string
}

export type ActivityGroupByEndUser = {
  endUserId: number
  activityList: ActivityForContact[]
  endUserData: ActivityEndUser
  lastActivityAt: number
} & ActivityEndUser

export interface ActivityCounts {
  CONVERSATION?: number
  EMAIL_CLICK?: number
  EMAIL_OPEN?: number
  EMAIL_SEND?: number // TODO: Remove me
  END_USER_IDENTIFIED?: number
  MEETING_BOOKED?: number
  MEETING_CANCELED?: number
  MEETING_RESCHEDULED?: number
  PAGE_VIEW?: number
  VIDEO?: number
}

export interface EngagementByWeek {
  weekStart: string
  engagementByType: any
}

export interface EngagementByDay {
  startOfDay: number
  engagementByType: any
}

export interface EngagementSummary {
  orgId: number
  accountId: string
  engagementByWeek: EngagementByWeek[]
  engagementByDay: EngagementByDay[]
}

export interface MeetingRecordingBody {
  orgId: number
  videoId: number
  driftUserId: number
  zoomUUID?: string
  title?: string
  videoPublicUrl?: string
  videoEmbedLink: string
  schedulingEvent?: {
    meetingType: string
    zoomInfo?: {
      topic: string
    }
  }
  participants: ActivityEndUser[]
}
